import { Location } from 'history';
import {
  AUTH_TOKEN_NAME,
  AUTH_TOKEN_EXPIRY_TIME_NAME,
  AUTH_ENDPOINT,
  ATTEMPTS_REJECTED_COUNT_NAME,
  ATTEMPTS_REJECTED_COUNT_MAX,
  AUTH_ENDPOINT_NEW_FLOW
} from './constants';
import * as localStorage from './localStorage';
import { assign } from './browser';
import { getItem as getLocalStorageItem, setItem as setLocalStorageItem } from './localStorage';

export const DEFAULT_EXPIRES_IN = 24 * 60 * 60;

/*
  Because `expiresIn` is generated on IDP side, there might be cases where there is a slight delay from
  the time the expiry is generated and the time we received it. So this will allow us to compensate it
*/
export const EXPIRY_THRESHOLD_IN_SECONDS = 10;

export function expiresInToExpiryTime(expiresIn: number) {
  return new Date(Date.now() + expiresIn * 1000).getTime();
}

export function storeTokenDetails(location: Location) {
  const urlParams = new URLSearchParams(location.hash.substr(1));

  /*
    IDP calls us back with access_token, id_token, and expires_in fields in case of success
    error and error_description in case of failure
  */
  const error = urlParams.get('error');
  const accessToken = urlParams.get('access_token');
  let expiresIn = Number(urlParams.get('expires_in'));

  if (error || !accessToken) {
    return false;
  }

  localStorage.setItem(AUTH_TOKEN_NAME, accessToken);

  expiresIn = (!isNaN(expiresIn) && expiresIn > 0 && expiresIn) || DEFAULT_EXPIRES_IN;

  localStorage.setItem(
    AUTH_TOKEN_EXPIRY_TIME_NAME,
    expiresInToExpiryTime(expiresIn - EXPIRY_THRESHOLD_IN_SECONDS).toString()
  );

  return true;
}

export function getToken() {
  const accessToken = localStorage.getItem(AUTH_TOKEN_NAME);

  if (!accessToken) {
    return null;
  }

  return accessToken;
}

export function getUserIdFromToken() {
  const accessToken = getToken();
  if (!accessToken) {
    return null;
  }
  try {
    const { user_id: userId } = JSON.parse(atob(accessToken.split('.')[1]));
    return userId;
  } catch (err) {
    return null;
  }
}

export function isAuthorized() {
  const accessToken = localStorage.getItem(AUTH_TOKEN_NAME);
  const expiryTime = localStorage.getItem(AUTH_TOKEN_EXPIRY_TIME_NAME);

  if (!accessToken || !expiryTime || isNaN(Number(expiryTime))) {
    return false;
  }

  const expiredToken = Number(expiryTime) < Date.now();

  return !expiredToken;
}

export function resetRejectedTokenAttempts() {
  setLocalStorageItem(ATTEMPTS_REJECTED_COUNT_NAME, `0`);
}

export function incrementRejectedTokenAttempts() {
  const attempts = Number(getLocalStorageItem(ATTEMPTS_REJECTED_COUNT_NAME) || 0) + 1; //in case of invalid tokens
  setLocalStorageItem(ATTEMPTS_REJECTED_COUNT_NAME, `${attempts}`);
}

export function isMaxRejectedAttemptsEcxeeded() {
  return Number(getLocalStorageItem(ATTEMPTS_REJECTED_COUNT_NAME)) > ATTEMPTS_REJECTED_COUNT_MAX;
}

export function removeTokenDetails() {
  localStorage.removeItem(AUTH_TOKEN_NAME);
  localStorage.removeItem(AUTH_TOKEN_EXPIRY_TIME_NAME);
}

export function loginSilently() {
  assign(AUTH_ENDPOINT);
}

export function loginWithPrompt() {
  assign(`${AUTH_ENDPOINT}&prompt=login`);
}

export function tryToLogin() {
  const accessToken = localStorage.getItem(AUTH_TOKEN_NAME);
  const expiryTime = localStorage.getItem(AUTH_TOKEN_EXPIRY_TIME_NAME);

  removeTokenDetails();

  if (!accessToken || !expiryTime) {
    // As there is no access toke, tryToLogin credentials are required
    loginWithPrompt();
  } else {
    // Refresh the access token, no need to enter credentials
    loginSilently();
  }
}

export function tryToLoginWithPhoneNumberFlow() {
  const accessToken = localStorage.getItem(AUTH_TOKEN_NAME);
  const expiryTime = localStorage.getItem(AUTH_TOKEN_EXPIRY_TIME_NAME);

  removeTokenDetails();

  if (!accessToken || !expiryTime) {
    // As there is no access toke, tryToLogin credentials are required
    assign(`${AUTH_ENDPOINT_NEW_FLOW}&prompt=login`);
  } else {
    // Refresh the access token, no need to enter credentials
    assign(AUTH_ENDPOINT_NEW_FLOW);
  }
}
